/**
 * Theme mode hook with localStorage persistence.
 * Supports light/dark + retro (Easter egg unlock).
 */

import { useState, useEffect, useCallback } from 'react';
import type { PaletteMode } from '@mui/material';
import type { AppThemeMode } from '@/contexts/ThemeContext';
import { analytics } from '@/lib/analytics';

const STORAGE_KEY = 'theme-mode';
const RETRO_KEY = 'theme-retro';

export function useThemeMode() {
  const [mode, setMode] = useState<PaletteMode>('light');
  const [isRetro, setIsRetro] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const stored = localStorage.getItem(STORAGE_KEY);

    if (stored === 'dark' || stored === 'light') {
      setMode(stored);
    } else {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      setMode(prefersDark ? 'dark' : 'light');
    }

    setIsRetro(localStorage.getItem(RETRO_KEY) === 'true');
    setIsHydrated(true);
  }, []);

  const toggleMode = useCallback(() => {
    setMode(prevMode => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      if (typeof window !== 'undefined') {
        localStorage.setItem(STORAGE_KEY, newMode);
      }
      analytics.themeChanged(prevMode, newMode, 'toggle');
      return newMode;
    });
  }, []);

  const setThemeMode = useCallback((newMode: PaletteMode) => {
    setMode(newMode);
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, newMode);
    }
  }, []);

  const setRetro = useCallback((on: boolean) => {
    setIsRetro(on);
    if (typeof window !== 'undefined') {
      localStorage.setItem(RETRO_KEY, String(on));
    }
    analytics.retroModeToggled(on);
  }, []);

  const appMode: AppThemeMode = isRetro ? 'retro' : mode;

  return {
    mode,
    appMode,
    isRetro,
    toggleMode,
    setMode: setThemeMode,
    setRetro,
    isHydrated,
  };
}
